import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Typography,
  Alert,
  Input,
  Select,
  Button,
  Spin,
  Progress,
  Space,
  message,
  Row,
  Col,
  Tag,
  Avatar,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  PictureOutlined,
  SaveOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CameraOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { uploadAvatar, deleteUploadedFile, getImageUrl } from '../../services/uploadService';
import { usersService } from '../../services/usersService';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'customer' | 'technician' | 'manager' | 'admin' | 'super_admin' | 'agent';
  status: 'active' | 'inactive' | 'pending';
  password: string;
  confirmPassword: string;
}

const initialState: UserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: 'customer',
  status: 'active',
  password: '',
  confirmPassword: ''
};

const getRoleTag = (role?: string) => {
  switch (role) {
    case 'super_admin':
      return <Tag color="purple" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Super Admin</Tag>;
    case 'admin':
      return <Tag color="red" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Administrateur</Tag>;
    case 'manager':
      return <Tag color="blue" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Team Lead</Tag>;
    case 'agent':
      return <Tag color="cyan" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Agent</Tag>;
    case 'technician':
      return <Tag color="orange" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Technicien</Tag>;
    case 'customer':
      return <Tag color="green" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Client</Tag>;
    default:
      return null;
  }
};

const getStatusTag = (status?: string) => {
  switch (status) {
    case 'active':
      return <Tag color="success" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>✓ Actif</Tag>;
    case 'inactive':
      return <Tag color="default" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Inactif</Tag>;
    case 'pending':
      return <Tag color="warning" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>⏳ En attente</Tag>;
    default:
      return null;
  }
};

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const isEditMode = Boolean(id);
  const isCurrentUserAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  
  const roleParam = searchParams.get('role');
  const initialRole = (!isEditMode && roleParam && ['customer', 'technician', 'manager', 'admin', 'super_admin', 'agent'].includes(roleParam))
    ? (roleParam as UserFormData['role'])
    : 'customer';

  const [formData, setFormData] = useState<UserFormData>({
    ...initialState,
    role: initialRole
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [avatarTimestamp, setAvatarTimestamp] = useState<number>(Date.now());
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Charger les données utilisateur en mode édition
  useEffect(() => {
    if (isEditMode && id) {
      const loadUserData = async () => {
        try {
          setLoading(true);
          const userData = await usersService.getUserById(parseInt(id));
          setFormData({
            firstName: userData.first_name || '',
            lastName: userData.last_name || '',
            email: userData.email || '',
            phone: userData.phone || '',
            role: userData.role as 'customer' | 'technician' | 'manager' | 'admin' | 'super_admin' | 'agent',
            status: userData.status as 'active' | 'inactive' | 'pending',
            password: '',
            confirmPassword: ''
          });
          setAvatarUrl(userData.profile_image || undefined);
          setAvatarTimestamp(Date.now());
        } catch (err: any) {
          setError(err?.response?.data?.message || 'Erreur lors du chargement de l\'utilisateur');
        } finally {
          setLoading(false);
        }
      };
      loadUserData();
    }
  }, [isEditMode, id]);

  const handleAvatarUpload = async (file: File) => {
    try {
      const result = await uploadAvatar(file, isEditMode && id ? id : undefined);
      setAvatarUrl(result.url);
      setAvatarTimestamp(Date.now());
      
      // Mettre à jour l'utilisateur en base si on est en mode édition
      if (isEditMode && id) {
        await usersService.updateUser(parseInt(id), {
          profile_image: result.url
        });
      }
      
      message.success('Avatar uploadé avec succès');
      return result;
    } catch (error) {
      console.error('Erreur upload avatar:', error);
      message.error('Erreur lors de l\'upload de l\'avatar');
      throw error;
    }
  };

  const handleAvatarDelete = async () => {
    if (avatarUrl) {
      const filename = avatarUrl.split('/').pop();
      if (filename) {
        await deleteUploadedFile('avatar', filename);
        setAvatarUrl(undefined);
        setAvatarTimestamp(Date.now());
        // Mettre à jour le profil côté backend pour supprimer l'image
        if (isEditMode && id) {
          await usersService.updateUser(parseInt(id), { profile_image: null });
        }
        message.success('Avatar supprimé');
      }
    }
  };

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation de la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('La taille du fichier ne doit pas dépasser 5 Mo');
      return;
    }

    // Validation du format image
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      message.error('Formats acceptés : JPEG, PNG, GIF ou WEBP');
      return;
    }

    setUploadingAvatar(true);
    try {
      await handleAvatarUpload(file);
    } catch (err) {
      // Déjà traité dans handleAvatarUpload
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onAvatarRemove = async () => {
    setUploadingAvatar(true);
    try {
      await handleAvatarDelete();
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'password') setPasswordStrength(calcPasswordStrength(value));
  };

  const handleRoleChange = (value: 'customer' | 'technician' | 'manager' | 'admin' | 'super_admin' | 'agent') => {
    setFormData(prev => ({ ...prev, role: value }));
  };

  const handleStatusChange = (value: 'active' | 'inactive' | 'pending') => {
    setFormData(prev => ({ ...prev, status: value }));
  };

  const calcPasswordStrength = (pwd: string): number => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 8) score += 30;
    if (/[A-Z]/.test(pwd)) score += 15;
    if (/[a-z]/.test(pwd)) score += 15;
    if (/[0-9]/.test(pwd)) score += 20;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 20;
    return Math.min(score, 100);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const firstName = formData.firstName || '';
    const lastName = formData.lastName || '';
    const phone = formData.phone || '';
    const email = formData.email || '';

    if (!firstName.trim()) newErrors.firstName = 'Prénom requis';
    if (!lastName.trim()) newErrors.lastName = 'Nom requis';
    if (!phone.trim()) newErrors.phone = 'Téléphone requis';
    else if (!/^[0-9+\s-]{8,20}$/.test(phone.replace(/\s/g, ''))) newErrors.phone = 'Numéro de téléphone invalide';
    
    // Email optionnel mais doit être valide si renseigné
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) newErrors.email = 'Email invalide';
    
    // En mode édition, le mot de passe est optionnel
    if (!isEditMode) {
      if (!formData.password) newErrors.password = 'Mot de passe requis';
      else if (formData.password.length < 8) newErrors.password = 'Minimum 8 caractères';
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirmation requise';
      else if (formData.confirmPassword !== formData.password) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    } else if (formData.password || formData.confirmPassword) {
      // En mode édition, si un mot de passe est saisi, on valide
      if (formData.password && formData.password.length < 8) newErrors.password = 'Minimum 8 caractères';
      if (formData.confirmPassword !== formData.password) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    if (!validate()) return;
    
    try {
      setSubmitting(true);
      
      if (isEditMode && id) {
        // Mode édition
        const updateData: any = {};
        if (formData.email) updateData.email = formData.email;
        if (formData.phone) updateData.phone = formData.phone;
        if (formData.role) updateData.role = formData.role;
        if (formData.status) updateData.status = formData.status;
        if (formData.firstName) updateData.first_name = formData.firstName;
        if (formData.lastName) updateData.last_name = formData.lastName;
        if (formData.password) updateData.password = formData.password;
        if (avatarUrl) updateData.profile_image = avatarUrl;

        await usersService.updateUser(parseInt(id), updateData);
        message.success('Utilisateur modifié avec succès !');
        navigate(`/users/${id}`);
      } else {
        // Mode création
        await usersService.createUser({
          email: formData.email || undefined,
          password: formData.password,
          phone: formData.phone,
          role: formData.role,
          status: formData.status,
          first_name: formData.firstName,
          last_name: formData.lastName,
          profile_image: avatarUrl || undefined
        });
        message.success('Utilisateur créé avec succès !');
        navigate('/users');
      }
    } catch (err: any) {
      console.error(err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || `Erreur lors de ${isEditMode ? 'la modification' : 'la création'}`;
      setError(errMsg);
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '80px 24px', textAlign: 'center', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#64748B', fontWeight: 500 }}>Chargement des données utilisateur...</div>
      </div>
    );
  }

  const userInitials = formData.firstName
    ? `${formData.firstName.charAt(0)}${formData.lastName ? formData.lastName.charAt(0) : ''}`.toUpperCase()
    : 'U';

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      
      {/* 1. Header Card avec Contexte & Actions Rapides (Fixe au Scroll) */}
      <Card
        style={{
          position: 'sticky',
          top: 64,
          zIndex: 90,
          borderRadius: 12,
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(8px)',
          marginBottom: 24,
        }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(isEditMode && id ? `/users/${id}` : '/users')}
              style={{ borderRadius: 8 }}
            >
              Retour
            </Button>
            <Avatar
              size={54}
              src={avatarUrl ? `${getImageUrl(avatarUrl)}?t=${avatarTimestamp}` : undefined}
              style={{
                backgroundColor: '#E8F5E9',
                color: '#0A543D',
                fontWeight: 700,
                fontSize: 20,
                border: '2px solid #C8E6C9',
              }}
            >
              {userInitials}
            </Avatar>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Title level={4} style={{ margin: 0, color: '#0F172A', fontWeight: 700 }}>
                  {isEditMode ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
                </Title>
                {isEditMode && getStatusTag(formData.status)}
                {isEditMode && getRoleTag(formData.role)}
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {isEditMode
                  ? `${formData.firstName} ${formData.lastName} • ID: #${id}`
                  : 'Création d\'un nouveau profil utilisateur dans le système'}
              </Text>
            </div>
          </div>

          <Space size="middle">
            <Button
              onClick={() => navigate(isEditMode && id ? `/users/${id}` : '/users')}
              disabled={submitting}
              style={{ borderRadius: 8 }}
            >
              Annuler
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={submitting}
              onClick={() => handleSubmit()}
              style={{
                backgroundColor: '#0A543D',
                borderColor: '#0A543D',
                borderRadius: 8,
                fontWeight: 600,
              }}
            >
              {isEditMode ? 'Enregistrer les modifications' : 'Créer l\'utilisateur'}
            </Button>
          </Space>
        </div>
      </Card>

      {/* Alerte d'erreur globale */}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24, borderRadius: 8 }}
        />
      )}

      {/* Formulaire principal */}
      <form onSubmit={handleSubmit} noValidate>

        {/* 2. Carte Identité & Informations Compte */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: '#E8F5E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0A543D',
              }}>
                <UserOutlined style={{ fontSize: 16 }} />
              </div>
              <span style={{ fontWeight: 600, color: '#0F172A', fontSize: 16 }}>
                Informations Personnelles & Compte
              </span>
            </div>
          }
          style={{
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            marginBottom: 24,
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  Prénom <span style={{ color: '#ff4d4f' }}>*</span>
                </label>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  status={errors.firstName ? 'error' : ''}
                  placeholder="Ex: Tidiane"
                  prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                  style={{ borderRadius: 8, height: 40 }}
                />
                {errors.firstName && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{errors.firstName}</div>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  Nom <span style={{ color: '#ff4d4f' }}>*</span>
                </label>
                <Input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  status={errors.lastName ? 'error' : ''}
                  placeholder="Ex: SAMASSI"
                  prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                  style={{ borderRadius: 8, height: 40 }}
                />
                {errors.lastName && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{errors.lastName}</div>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  Email <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 400 }}>(optionnel)</span>
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  status={errors.email ? 'error' : ''}
                  placeholder="utilisateur@domaine.ci"
                  prefix={<MailOutlined style={{ color: '#94A3B8' }} />}
                  style={{ borderRadius: 8, height: 40 }}
                />
                {errors.email && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{errors.email}</div>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  Téléphone <span style={{ color: '#ff4d4f' }}>*</span>
                </label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  status={errors.phone ? 'error' : ''}
                  placeholder="Ex: 2250501020304"
                  prefix={<PhoneOutlined style={{ color: '#94A3B8' }} />}
                  style={{ borderRadius: 8, height: 40 }}
                />
                {errors.phone && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{errors.phone}</div>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  Rôle <span style={{ color: '#ff4d4f' }}>*</span>
                </label>
                <Select
                  value={formData.role}
                  onChange={handleRoleChange}
                  style={{ width: '100%' }}
                  size="large"
                  options={[
                    { value: 'customer', label: 'Client' },
                    { value: 'technician', label: 'Technicien' },
                    // Seul un admin ou super_admin peut créer un manager, agent ou admin
                    ...(isCurrentUserAdmin ? [
                      { value: 'agent', label: 'Agent' },
                      { value: 'manager', label: 'Team Lead' },
                      { value: 'admin', label: 'Administrateur' },
                    ] : []),
                  ]}
                />
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  Statut du compte <span style={{ color: '#ff4d4f' }}>*</span>
                </label>
                <Select
                  value={formData.status}
                  onChange={handleStatusChange}
                  style={{ width: '100%' }}
                  size="large"
                  options={[
                    { value: 'active', label: '✓ Actif' },
                    { value: 'pending', label: '⏳ En attente' },
                    { value: 'inactive', label: 'Inactif' },
                  ]}
                />
              </div>
            </Col>
          </Row>
        </Card>

        {/* 3. Carte Sécurité & Mot de Passe */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: '#E8F5E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0A543D',
              }}>
                <SafetyCertificateOutlined style={{ fontSize: 16 }} />
              </div>
              <div>
                <span style={{ fontWeight: 600, color: '#0F172A', fontSize: 16 }}>
                  Sécurité & Authentification
                </span>
                <span style={{ fontSize: 12, color: '#64748B', marginLeft: 8, fontWeight: 400 }}>
                  {isEditMode ? '(Laissez vide pour conserver le mot de passe actuel)' : '(Requis)'}
                </span>
              </div>
            </div>
          }
          style={{
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            marginBottom: 24,
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  {isEditMode ? 'Nouveau mot de passe' : 'Mot de passe'} {!isEditMode && <span style={{ color: '#ff4d4f' }}>*</span>}
                </label>
                <Input.Password
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  status={errors.password ? 'error' : ''}
                  placeholder={isEditMode ? 'Laisser vide pour ne pas changer' : 'Minimum 8 caractères'}
                  prefix={<LockOutlined style={{ color: '#94A3B8' }} />}
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                  style={{ borderRadius: 8, height: 40 }}
                />
                {errors.password && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{errors.password}</div>
                )}
                {formData.password && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Force du mot de passe</Text>
                      <Text style={{ fontSize: 11, fontWeight: 600, color: passwordStrength < 50 ? '#ff4d4f' : passwordStrength < 70 ? '#faad14' : '#52c41a' }}>
                        {passwordStrength < 50 ? 'Faible' : passwordStrength < 70 ? 'Moyen' : 'Fort'}
                      </Text>
                    </div>
                    <Progress
                      percent={passwordStrength}
                      strokeColor={passwordStrength < 50 ? '#ff4d4f' : passwordStrength < 70 ? '#faad14' : '#52c41a'}
                      showInfo={false}
                      size="small"
                    />
                  </div>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#334155' }}>
                  {isEditMode ? 'Confirmer nouveau mot de passe' : 'Confirmer le mot de passe'} {!isEditMode && <span style={{ color: '#ff4d4f' }}>*</span>}
                </label>
                <Input.Password
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  status={errors.confirmPassword ? 'error' : ''}
                  placeholder="Répétez le mot de passe"
                  prefix={<LockOutlined style={{ color: '#94A3B8' }} />}
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                  style={{ borderRadius: 8, height: 40 }}
                />
                {errors.confirmPassword && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{errors.confirmPassword}</div>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        {/* 4. Carte Photo de Profil (Design Compact & Simple) */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: '#E8F5E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0A543D',
              }}>
                <PictureOutlined style={{ fontSize: 16 }} />
              </div>
              <span style={{ fontWeight: 600, color: '#0F172A', fontSize: 16 }}>
                Photo de profil
              </span>
            </div>
          }
          style={{
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            marginBottom: 24,
          }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: 76, height: 76, flexShrink: 0 }}>
              <Avatar
                size={76}
                src={avatarUrl ? `${getImageUrl(avatarUrl)}?t=${avatarTimestamp}` : undefined}
                style={{
                  backgroundColor: '#E8F5E9',
                  color: '#0A543D',
                  fontWeight: 700,
                  fontSize: 26,
                  border: avatarUrl ? '2px solid #0A543D' : '2px dashed #A7F3D0',
                }}
              >
                {userInitials}
              </Avatar>
              {uploadingAvatar && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Spin size="small" />
                </div>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 260 }}>
              <Space size="middle" style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                <Button
                  icon={<CameraOutlined />}
                  onClick={() => fileInputRef.current?.click()}
                  loading={uploadingAvatar}
                  style={{
                    borderRadius: 8,
                    borderColor: '#0A543D',
                    color: '#0A543D',
                    fontWeight: 500,
                  }}
                >
                  {avatarUrl ? 'Changer la photo' : 'Ajouter une photo'}
                </Button>

                {avatarUrl && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={onAvatarRemove}
                    loading={uploadingAvatar}
                    style={{ borderRadius: 8 }}
                  >
                    Supprimer
                  </Button>
                )}
              </Space>

              <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>
                Formats acceptés : JPG, PNG, GIF ou WEBP (max. 5 Mo). Recommandé : image carrée et nette.
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={onFileSelect}
                style={{ display: 'none' }}
                disabled={uploadingAvatar}
              />
            </div>
          </div>
        </Card>

        {/* 5. Barre d'actions inférieure (Fixe au Scroll) */}
        <Card
          style={{
            position: 'sticky',
            bottom: 16,
            zIndex: 80,
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 -4px 16px rgba(15, 23, 42, 0.08)',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(8px)',
          }}
          bodyStyle={{ padding: '16px 24px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              <span style={{ color: '#ff4d4f' }}>*</span> Champs obligatoires
            </Text>
            <Space size="middle">
              <Button
                onClick={() => navigate(isEditMode && id ? `/users/${id}` : '/users')}
                disabled={submitting}
                style={{ borderRadius: 8 }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<SaveOutlined />}
                style={{
                  backgroundColor: '#0A543D',
                  borderColor: '#0A543D',
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                {isEditMode ? 'Enregistrer les modifications' : 'Créer l\'utilisateur'}
              </Button>
            </Space>
          </div>
        </Card>

      </form>
    </div>
  );
};

export default UserForm;
